import isEqual from 'lodash/isEqual'

import { t } from '@/i18n/i18n'
import { PageContentService } from '@/pages/menuEntries/pageSetup/pageContentService'
import { usePageSetupStore } from '@/pages/menuEntries/pageSetup/pageSetup.store'
import router from '@/router'
import { PAGE_MODE, type PageMode } from '@/shared/constants'
import { ErrorService } from '@/shared/service/errorService'
import {
  FilesService,
  type ImageTransformations,
} from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { type DataOptions } from '@/shared/types/dataOptions.type'
import { InternalType } from '@/shared/types/files.type'
import { type MusicGroup } from '@/shared/types/musicGroup/musicGroup.type'
import { type MusicGroupEventFormInput } from '@/shared/types/musicGroup/musicGroupEvent.type'
import { ProgramEventNotifSendTime } from '@/shared/types/progEvent.type'
import { create } from '@/shared/utils/zustandVue'
import {
  cloneDeep,
  isNotNullish,
  keys,
  mapByEntries,
  type Optional,
} from '@/utils'

import { type TranslatableText } from '../../../../types/TranslatableText.type'
import { type SocialLinks } from '../../../shared/service/festival.service'
import { type GuestDto } from '../../../shared/types/musicGroup/guestDto.type'
import { ProgEventService } from '../../progEvent/progEvent.service'
import { GuestsService } from '../guests.service'

const defaultInitGuestEvent: Partial<MusicGroupEventFormInput> = {
  scene: undefined,
  day: undefined,
  eventTicketLink: undefined,
  startTime: '12:00',
  endTime: '13:00',
  date: undefined,
  isVisible: false,
  program: undefined,
  enableAttending: false,
  enableRating: false,
  programEventNotifSendTime: ProgramEventNotifSendTime.NEVER,
  notifPlanned: false,
  ticketOpenInWebview: true,
}

export class GuestsState {
  mode: PageMode = PAGE_MODE.VIEW
  guests: Record<string, MusicGroup> = {}
  nbGuests = 0
  countByType = {}
  deleteGuestId: string | null = null
  togglingGuest: { guestId: string; names: TranslatableText } | null = null
  showHideMode: 'SHOW' | 'HIDE' = 'HIDE'
  selectedGuestRows = [] as MusicGroup[]
  loading = false
  search = ''
  selectedGuestTypeId: string | undefined
  options: Optional<
    DataOptions,
    'groupBy' | 'groupDesc' | 'multiSort' | 'mustSort'
  > = {
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true],
    sortBy: ['names.fr'],
  }

  //Guest form
  editingGuest: MusicGroup | GuestDto | undefined = undefined
  links = {} as Record<
    string,
    {
      value: SocialLinks
      url: string
      text: string
    }
  >
  image: string | null = null
  guestsLinks: string[] = []

  //for Events
  guestsIdsNames: Pick<
    MusicGroup,
    | '_id'
    | 'names'
    | 'descriptions'
    | 'image'
    | 'copyright'
    | 'genres'
    | 'weight'
    | 'lineUp'
    | 'showArtist'
  >[] = []

  // location
  autocompleteLocations: string[][] = []
  //
  transformations: ImageTransformations | null = null
  importBatchId = ''
  importFeedback?: {
    successes: { key: string; content: Record<string, string> }[]
    errors: { key: string; content: Record<string, string> }[]
    toProcess: { key: string; content: Record<string, string> }[]
  }

  // Guest events
  guestEventEditingOldValue: MusicGroupEventFormInput | null = null
  currentGuestEvent: Partial<MusicGroupEventFormInput> | null = null
  guestEvents: MusicGroupEventFormInput[] = []
  deletingGuestEvents: MusicGroupEventFormInput[] = []
  deletingGuestMarkers: { _id: string; name: string }[] = []
  initGuestEvent = { ...defaultInitGuestEvent }

  // Request-sequence token: stale search responses are discarded by mismatch.
  _searchRequestId = 0
}

type GuestsStore = GuestsState & {
  load: () => void
  unload: () => void
  setGuests: (payload: {
    musicGroups: MusicGroup[]
    total: number
    countByType: Record<string, number>
  }) => void
  setImageTransformations: (
    transformations: ImageTransformations | null,
  ) => void
  setView: () => void
  setAdding: () => void
  setEditing: (guest: MusicGroup) => void
  setNotDeleting: () => void
  setNotTogglingVisibility: () => void
  setDeletingMultipleGuests: () => void
  setEditMultipleGuests: () => void
  resetMultiSelection: () => void
  setSearch: (search: string) => void
  setSelectedGuestTypeId: (selectedGuestTypeId: string | undefined) => void
  setSelectedGuestRows: (guests: MusicGroup[]) => void
  setImage: (image: string | null) => void
  setDeleteLink: (linkValue: string) => void
  setLinkUrl: (payload: { link: { value: string }; val: string }) => void
  loadLinks: () => void
  addLink: (link: GuestsState['links'][string]) => void
  setOptions: (options: GuestsState['options']) => void
  resetPagination: () => void
  setImportFeedback: (importResult: GuestsState['importFeedback']) => void
  setImportBatchId: (importBatchId: string) => void
  addGuestEvent: () => void
  editGuestEvent: (guestEvent: MusicGroupEventFormInput) => void
  updateGuestEvent: (guestEvent: MusicGroupEventFormInput) => void
  cancelGuestEvent: () => void
  saveGuestEvent: () => void
  deleteGuestEvent: (guestEvent: MusicGroupEventFormInput) => void
  setGuestEvents: (events: MusicGroupEventFormInput[]) => void
  setGuestMarkers: (markers: { _id: string; name: string }[]) => void
  list: () => Promise<void>
  getGuestsLinks: () => Promise<void>
  getGuestsLight: () => Promise<void>
  getPaginatedGuests: () => Promise<void>
  delete: () => Promise<void>
  multipleDelete: () => Promise<void>
  toggleVisibility: (payload: { eventsIds: string[] }) => Promise<void>
  setVisibilityBatch: (payload: { eventsIds: string[] }) => Promise<void>
  uploadImage: (file: File | null) => Promise<void>
  addGuest: (guest: MusicGroup) => Promise<void>
  updateGuestWeight: (payload: {
    guestId: string
    weight: number | null
  }) => Promise<void>
  loadGuestsLocations: () => Promise<void>
  loadGuest: (guestId: string) => Promise<void>
  saveFile: (file: File, visible: boolean) => Promise<void>
  updateImportList: (batchId: string) => Promise<void>
  resetImport: () => void
  setDeletionMode: (guestId: string) => Promise<void>
  setToggleVisibilityMode: (payload: {
    guestId: string
    show: boolean
    names: TranslatableText
  }) => Promise<void>
  setMultipleDeletionMode: () => Promise<void>
  setMultipleVisibilityMode: (show: boolean) => Promise<void>
  editGuestsBatch: (typeId: string | undefined) => Promise<void>
  reset: () => void
}

export const useGuestsStore = create<GuestsStore>((set, get) => ({
  ...new GuestsState(),

  load: () => {
    set({ loading: true })
  },

  unload: () => {
    set({ loading: false })
  },

  setGuests: ({ musicGroups, total, countByType }) => {
    set({
      guests: musicGroups.reduce((acc: Record<string, MusicGroup>, guest) => {
        if (guest._id == null) return acc
        acc[guest._id] = guest
        return acc
      }, {}),
      nbGuests: total,
      countByType,
    })
  },

  setImageTransformations: (transformations) => {
    set({ transformations })
  },

  setView: () => {
    set({
      mode: PAGE_MODE.VIEW,
      deleteGuestId: null,
      selectedGuestRows: [],
      editingGuest: undefined,
      image: null,
      loading: false,
      search: '',
      links: {},
      guestEvents: [],
    })
  },

  setAdding: () => {
    set({
      mode: PAGE_MODE.ADD,
      loading: false,
      guestEvents: [],
      currentGuestEvent: null,
      initGuestEvent: { ...defaultInitGuestEvent },
    })
  },

  setEditing: (guest) => {
    set({
      mode: PAGE_MODE.EDIT,
      editingGuest: { ...guest },
      image: guest.image ?? null,
      loading: false,
      initGuestEvent: { ...defaultInitGuestEvent },
    })
  },

  setNotDeleting: () => {
    set({ mode: PAGE_MODE.VIEW, deleteGuestId: null })
  },

  setNotTogglingVisibility: () => {
    set({ mode: PAGE_MODE.VIEW, togglingGuest: null })
  },

  setDeletingMultipleGuests: () => {
    set({ mode: PAGE_MODE.DELETE_MULTIPLE })
  },

  setEditMultipleGuests: () => {
    set({ mode: PAGE_MODE.EDIT_MULTIPLE })
  },

  resetMultiSelection: () => {
    set({ selectedGuestRows: [] })
  },

  setSearch: (search) => {
    set((s) => ({ search, options: { ...s.options, page: 1 } }))
  },

  setSelectedGuestTypeId: (selectedGuestTypeId) => {
    set((s) => ({
      selectedGuestTypeId,
      options: { ...s.options, page: 1 },
    }))
  },

  setSelectedGuestRows: (guests) => {
    set({ selectedGuestRows: [...guests] })
  },

  setImage: (image) => {
    set({ image, loading: false })
  },

  setDeleteLink: (linkValue) => {
    set((s) => {
      const { [linkValue]: _removed, ...links } = s.links
      return { links }
    })
  },

  setLinkUrl: ({ link, val }) => {
    set((s) => ({
      links: {
        ...s.links,
        [link.value]: { ...s.links[link.value], url: val },
      },
    }))
  },

  loadLinks: () => {
    set((s) => {
      const { editingGuest } = s
      const tmpLinks: { [key in SocialLinks]?: string } =
        s.mode !== PAGE_MODE.EDIT || editingGuest == null
          ? s.guestsLinks.reduce<Record<string, string>>((acc, link) => {
              acc[link] = ''
              return acc
            }, {})
          : keys(editingGuest.links)
              .filter((key) => editingGuest.links[key] != null)
              .reduce<{ [key in SocialLinks]?: string }>((acc, key) => {
                const linkVal = editingGuest.links[key]
                if (linkVal != null) {
                  acc[key] = linkVal
                }
                return acc
              }, {})
      return {
        links: mapByEntries(
          tmpLinks,
          ([key, value]) =>
            [
              key,
              {
                value: key,
                url: value ?? '',
                text: t(`GUESTS.ADDING.LINKS_LIST.${key.toUpperCase()}`),
              },
            ] as const,
        ),
      }
    })
  },

  addLink: (link) => {
    set((s) => ({ links: { ...s.links, [link.value]: link } }))
  },

  setOptions: (options) => {
    set({ options })
  },

  resetPagination: () => {
    set((s) => ({
      options: { ...s.options, page: 1, itemsPerPage: 10 },
    }))
  },

  setImportFeedback: (importResult) => {
    set({ importFeedback: importResult })
  },

  setImportBatchId: (importBatchId) => {
    set({ importBatchId })
  },

  // Guest Events

  addGuestEvent: () => {
    set((s) => ({
      guestEventEditingOldValue: null,
      currentGuestEvent: cloneDeep(s.initGuestEvent),
    }))
  },

  editGuestEvent: (guestEvent) => {
    set({
      guestEventEditingOldValue: cloneDeep(guestEvent),
      currentGuestEvent: cloneDeep(guestEvent),
    })
  },

  updateGuestEvent: (guestEvent) => {
    set({ currentGuestEvent: cloneDeep(guestEvent) })
  },

  cancelGuestEvent: () => {
    set({ guestEventEditingOldValue: null, currentGuestEvent: null })
  },

  saveGuestEvent: () => {
    set((s) => {
      let { guestEvents } = s
      if (s.guestEventEditingOldValue != null) {
        guestEvents = guestEvents.filter(
          (e) => !isEqual(e, s.guestEventEditingOldValue),
        )
      }
      if (s.currentGuestEvent) {
        guestEvents = [
          ...guestEvents,
          cloneDeep(s.currentGuestEvent) as MusicGroupEventFormInput,
        ]
      }
      return {
        guestEvents,
        initGuestEvent: {
          ...cloneDeep(s.currentGuestEvent),
          date: undefined,
          eventTicketLink: null,
          startTime: '12:00',
          endTime: '13:00',
        },
        currentGuestEvent: null,
        guestEventEditingOldValue: null,
      }
    })
  },

  deleteGuestEvent: (guestEvent) => {
    set((s) => ({
      currentGuestEvent: null,
      guestEvents: s.guestEvents.filter((e) => !isEqual(e, guestEvent)),
    }))
  },

  setGuestEvents: (events) => {
    set({ deletingGuestEvents: [...events] })
  },

  setGuestMarkers: (markers) => {
    set({ deletingGuestMarkers: [...markers] })
  },

  async list() {
    get().load()
    const musicGroups = await GuestsService.getAll()
    get().setGuests({
      musicGroups,
      total: musicGroups.length,
      countByType: {},
    })
    get().unload()
  },

  async getGuestsLinks() {
    try {
      get().setAdding()
      get().load()
      const guests = await GuestsService.getGuestsLinks()
      set({ guestsLinks: guests })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async getGuestsLight() {
    try {
      get().load()
      const guests = await GuestsService.getGuestsLight()
      set({ guestsIdsNames: guests })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async getPaginatedGuests() {
    const requestId = ++get()._searchRequestId
    try {
      get().load()
      const s = get()
      const offset = (s.options.page - 1) * s.options.itemsPerPage
      const data = await GuestsService.getPaginatedGuests(
        s.search,
        s.options,
        offset,
        s.selectedGuestTypeId,
      )
      if (get()._searchRequestId !== requestId) return
      get().setGuests(data)
      get().resetMultiSelection()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      // An outdated request must not clear the loader while the newer one
      // is still pending.
      if (get()._searchRequestId === requestId) get().unload()
    }
  },

  async delete() {
    const s = get()
    if (s.deleteGuestId == null) return
    get().load()
    try {
      const guestName = s.guests[s.deleteGuestId].names.fr
      await GuestsService.deleteGuest(s.deleteGuestId)
      await get().getPaginatedGuests()
      SnackbarService.info(
        t('GUESTS.SNACK_BAR.DELETE', {
          string: guestName,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setView()
    }
  },

  async multipleDelete() {
    get().load()
    try {
      const guestsIds = get().selectedGuestRows.map((g) => g._id) as string[]
      await GuestsService.deleteMultipleGuests(guestsIds)
      await get().getPaginatedGuests()
      SnackbarService.info(
        t('GUESTS.SNACK_BAR.MULTIPLE_DELETE', {
          number: guestsIds.length,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setView()
    }
  },

  async toggleVisibility({ eventsIds }) {
    get().load()
    try {
      const s = get()
      if (!s.togglingGuest) return
      const togglingGuest = s.togglingGuest
      await GuestsService.hideShowBatch(
        [togglingGuest.guestId],
        s.showHideMode === 'SHOW',
      )
      await ProgEventService.hideShowBatch(eventsIds, s.showHideMode === 'SHOW')
      await get().getPaginatedGuests()
      const pageChoices = await PageContentService.getChoices()
      usePageSetupStore.getState().setPageChoices(pageChoices)
      if (s.showHideMode === 'SHOW') {
        SnackbarService.info(
          t('GUESTS.SNACK_BAR.SHOW', {
            string: togglingGuest.names.fr,
          }),
        )
      } else {
        SnackbarService.info(
          t('GUESTS.SNACK_BAR.HIDE', {
            string: togglingGuest.names.fr,
          }),
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setView()
    }
  },

  async setVisibilityBatch({ eventsIds }) {
    get().load()
    try {
      const s = get()
      const guestsIds = s.selectedGuestRows.map((g) => g._id) as string[]
      const nb = guestsIds.length
      await GuestsService.hideShowBatch(guestsIds, s.showHideMode === 'SHOW')
      await ProgEventService.hideShowBatch(eventsIds, s.showHideMode === 'SHOW')
      await get().getPaginatedGuests()
      const pageChoices = await PageContentService.getChoices()
      usePageSetupStore.getState().setPageChoices(pageChoices)
      if (s.showHideMode === 'SHOW') {
        SnackbarService.info(
          t('GUESTS.SNACK_BAR.BATCH_SHOW', {
            number: nb,
          }),
        )
      } else {
        SnackbarService.info(
          t('GUESTS.SNACK_BAR.BATCH_HIDE', {
            number: nb,
          }),
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setView()
    }
  },

  async uploadImage(file) {
    get().load()
    try {
      const filename = file
        ? (await FilesService.uploadImageFile(file, InternalType.PICTURE))
            .filename
        : null
      get().setImage(filename)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async addGuest(guest) {
    get().load()
    try {
      const s = get()
      const eventsCount = s.guestEvents.length
      const guestToSend = cloneDeep(guest)
      guestToSend.image = s.image ?? undefined
      guestToSend.links = {}
      Object.values(s.links).forEach((link) => {
        guestToSend.links[link.value] = link.url
      })

      let message
      if (s.mode === PAGE_MODE.EDIT) {
        await GuestsService.updateGuest(guestToSend, cloneDeep(s.guestEvents))
        message = t('GUESTS.SNACK_BAR.EDIT', {
          string: guest.names.fr,
        })
      } else {
        await GuestsService.addGuest(guestToSend, cloneDeep(s.guestEvents))
        message = t('GUESTS.SNACK_BAR.ADD', {
          string: guest.names.fr,
        })
      }

      if (s.transformations) {
        await FilesService.setImageTransform(
          s.transformations.imageId,
          s.transformations,
        )
      }

      await get().getPaginatedGuests()
      await router.push('/guests')
      SnackbarService.info(message)
      setTimeout(() => {
        if (eventsCount > 0) {
          SnackbarService.info(
            t('GUESTS.SNACK_BAR.EVENTS', {
              count: eventsCount,
            }),
          )
        }
      }, 3200)
    } catch (e) {
      ErrorService.handleError(e)
      throw e
    } finally {
      get().unload()
    }
  },

  async updateGuestWeight({
    guestId,
    weight,
  }: {
    guestId: string
    weight: number | null
  }) {
    try {
      await GuestsService.updateGuestWeight(guestId, weight)
      if (guestId in get().guests) {
        const guest = get().guests[guestId]
        SnackbarService.info(
          t('GUESTS.SNACK_BAR.EDIT', {
            string: guest.names.fr,
          }),
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
      await get().getPaginatedGuests() // revert on error
    }
  },

  async loadGuestsLocations() {
    try {
      get().load()
      const locations = await GuestsService.getGuestsLocations()
      set({ autocompleteLocations: locations })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async loadGuest(guestId) {
    get().load()
    try {
      const guest = await GuestsService.getGuestById(guestId)
      if (guest.image != null) {
        const transformations = await FilesService.getImageTransform(
          guest.image,
        )
        get().setImageTransformations(transformations)
      }
      get().setEditing(guest)
      get().loadLinks()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async saveFile(file, visible) {
    get().load()
    try {
      const x64File = await new Promise<string | ArrayBuffer | null>(
        (resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (): void => {
            resolve(reader.result)
          }
          reader.onerror = (error): void => {
            console.error(error)
            reject(
              error instanceof Error ? error : new Error('Error reading file'),
            )
          }
        },
      )
      const results = await GuestsService.uploadFile(x64File, visible)
      get().setImportBatchId(results.data)
      await get().updateImportList(results.data)
    } catch (e) {
      ErrorService.handleError(e)
      get().unload()
    }
  },

  async updateImportList(batchId) {
    const results = await GuestsService.getImportResult(batchId)
    get().setImportFeedback(results.data)
    // The backend may omit/null toProcess: guard it (Vue 2 parity), else the
    // polling throws and the loader never clears.
    const toProcess = results.data.toProcess as unknown[] | null | undefined
    if (toProcess != null && toProcess.length > 0) {
      setTimeout(async () => {
        await get().updateImportList(batchId)
      }, 7000)
    } else {
      get().unload()
    }
  },

  resetImport() {
    get().setImportFeedback({
      successes: [],
      errors: [],
      toProcess: [],
    })
    get().setImportBatchId('')
  },

  async setDeletionMode(guestId) {
    get().load()
    try {
      const eventList = await GuestsService.getGuestEvents([guestId])
      get().setGuestEvents(eventList)
      const markerList = await GuestsService.getGuestMarkers([guestId])
      get().setGuestMarkers(
        markerList.map((e) => ({ _id: e._id, name: e.fakeTitle })),
      )
      set({ mode: PAGE_MODE.DELETE, deleteGuestId: guestId })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async setToggleVisibilityMode({
    guestId,
    show,
    names,
  }: {
    guestId: string
    show: boolean
    names: TranslatableText
  }) {
    get().load()
    try {
      const eventList = await GuestsService.getGuestEvents([guestId])
      get().setGuestEvents(eventList)
      set({
        mode: PAGE_MODE.TOGGLING_VISIBILITY,
        togglingGuest: { guestId, names },
        showHideMode: show ? 'SHOW' : 'HIDE',
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async setMultipleDeletionMode() {
    get().load()
    try {
      const guestIds = get()
        .selectedGuestRows.map((g) => g._id)
        .filter(isNotNullish)
      try {
        const eventList = await GuestsService.getGuestEvents(guestIds)
        get().setGuestEvents(eventList)
      } catch (e) {
        ErrorService.handleError(e)
      }
      try {
        const markerList = await GuestsService.getGuestMarkers(guestIds)
        get().setGuestMarkers(
          markerList.map((e) => ({ _id: e._id, name: e.fakeTitle })),
        )
      } catch (e) {
        ErrorService.handleError(e)
      }
      get().setDeletingMultipleGuests()
    } finally {
      get().unload()
    }
  },

  async setMultipleVisibilityMode(show) {
    get().load()
    try {
      const guestIds = get()
        .selectedGuestRows.map((g) => g._id)
        .filter(isNotNullish)
      try {
        const eventList = await GuestsService.getGuestEvents(guestIds)
        get().setGuestEvents(eventList)
      } catch (e) {
        ErrorService.handleError(e)
      }
      set({
        mode: PAGE_MODE.SETTING_VISIBILITY_MULTIPLE,
        showHideMode: show ? 'SHOW' : 'HIDE',
      })
    } finally {
      get().unload()
    }
  },

  async editGuestsBatch(typeId) {
    get().load()
    try {
      const guestIds = get()
        .selectedGuestRows.map((g) => g._id)
        .filter(isNotNullish)
      await GuestsService.editGuestsBatch(guestIds, typeId)
      await get().getPaginatedGuests()
      SnackbarService.info(
        t('GUESTS.SNACK_BAR.BATCH_EDIT', {
          string: guestIds.length,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().resetMultiSelection()
      get().unload()
    }
  },
  reset: () => {
    set({ ...new GuestsState() })
  },
}))
